import { useState } from 'react';
import type { AccionDeAthena, AthenaAdjunto, AthenaMensaje, TipoDeAdjuntoDeAthena } from '@conversia/shared-types';
import {
  AlertCircle,
  ArrowUpRight,
  Check,
  Copy,
  File,
  FileSpreadsheet,
  FileText,
  FileUp,
  Film,
  FolderInput,
  HelpCircle,
  Inbox,
  Loader2,
  MessagesSquare,
  Mic,
  Moon,
  RotateCw,
  Search,
  Sparkles,
  Sunrise,
  UserRound,
  X,
  type LucideIcon,
} from 'lucide-react';
import { cn } from '@/lib/cn';
import { horaDeMensaje, sinMarcas, tamanoLegible } from '@/lib/athena-hilos';
import { TextoRico } from '@/lib/texto-rico';

/**
 * LAS PIEZAS DE LA CONVERSACIÓN CON ATHENA (tanda 6). Kervens: «como si fuese 100 % un mini
 * ChatGPT dentro de mi sistema». Todo lo de este archivo PINTA: recibe datos y avisa de lo que
 * se toca. Quién manda, qué se sube y qué hace cada botón lo decide la página.
 *
 * ═══ ATHENA NUNCA HACE NADA SOLA ═══
 * Sus botones (`AccionDeAthena`) los dispara la persona. Los que mueven algo (llevar un archivo,
 * importar un chat) se apagan mientras corren, y el de importar pregunta antes, en el sitio.
 */

const FOCO = 'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-600';

/** El avatar de Athena: el mismo destello del menú y del botón flotante. */
export function AvatarDeAthena({ className }: { className?: string }) {
  return (
    <span aria-hidden className={cn('bg-brand-gradient grid h-8 w-8 shrink-0 place-items-center rounded-full text-white shadow-sm shadow-brand-900/20', className)}>
      <Sparkles className="h-4 w-4" />
    </span>
  );
}

// ───────────────────────────── adjuntos ─────────────────────────────

const TIPO: Record<TipoDeAdjuntoDeAthena, { nombre: string; icono: LucideIcon; tono: string }> = {
  pdf: { nombre: 'PDF', icono: FileText, tono: 'bg-red-50 text-red-600 dark:bg-red-950/50 dark:text-red-300' },
  imagen: { nombre: 'Foto', icono: File, tono: 'bg-sky-50 text-sky-600 dark:bg-sky-950/50 dark:text-sky-300' },
  audio: { nombre: 'Audio', icono: Mic, tono: 'bg-brand-50 text-brand-600 dark:bg-brand-900/40 dark:text-brand-200' },
  video: { nombre: 'Video', icono: Film, tono: 'bg-amber-50 text-amber-600 dark:bg-amber-950/50 dark:text-amber-300' },
  excel: { nombre: 'Hoja de cálculo', icono: FileSpreadsheet, tono: 'bg-emerald-50 text-emerald-600 dark:bg-emerald-950/50 dark:text-emerald-300' },
  chat: { nombre: 'Chat de WhatsApp', icono: MessagesSquare, tono: 'bg-green-50 text-green-600 dark:bg-green-950/50 dark:text-green-300' },
  documento: { nombre: 'Documento', icono: File, tono: 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300' },
};

const CAJA = 'rounded-xl border border-surface-border bg-surface shadow-sm';
// Gris 600 y no 500: la hora va sobre el degradado violeta de la conversación, y con 500 no se lee bien.
const DATO = 'text-xs text-slate-600 dark:text-slate-400';

/**
 * Un archivo dentro de un mensaje: la foto se ve, el audio se oye (con lo que dice debajo, si se
 * pudo transcribir) y lo demás es una ficha que abre el archivo en otra pestaña.
 */
export function FichaDeAdjunto({ adjunto }: { adjunto: AthenaAdjunto }) {
  const { nombre: tipo, icono: Icono, tono } = TIPO[adjunto.tipo] ?? TIPO.documento;
  const peso = tamanoLegible(adjunto.tamano);

  if (adjunto.tipo === 'imagen') {
    return (
      <a
        href={adjunto.url}
        target="_blank"
        rel="noopener noreferrer"
        title={`Abrir ${adjunto.nombre}`}
        data-adjunto="imagen"
        className={cn(CAJA, FOCO, 'block w-60 max-w-full overflow-hidden transition-colors hover:border-brand-300 dark:hover:border-brand-500/60')}
      >
        <img src={adjunto.url} alt={adjunto.nombre} loading="lazy" className="h-40 w-full bg-slate-100 object-cover dark:bg-slate-800" />
        <span className="flex items-baseline gap-2 px-3 py-2">
          <span className="min-w-0 flex-1 truncate text-sm font-medium text-slate-800 dark:text-slate-100">{adjunto.nombre}</span>
          <span className={cn(DATO, 'shrink-0')}>{peso}</span>
        </span>
      </a>
    );
  }

  if (adjunto.tipo === 'audio') {
    return (
      <div data-adjunto="audio" className={cn(CAJA, 'w-80 max-w-full p-3')}>
        <div className="flex items-center gap-2.5">
          <span aria-hidden className={cn('grid h-9 w-9 shrink-0 place-items-center rounded-lg', tono)}>
            <Icono className="h-4 w-4" />
          </span>
          <span className="min-w-0">
            <span className="block truncate text-sm font-medium text-slate-800 dark:text-slate-100">{adjunto.nombre}</span>
            <span className={DATO}>
              {tipo} · {peso}
            </span>
          </span>
        </div>
        {/* `preload="none"`: un hilo con diez audios no baja los diez al abrirse. */}
        <audio controls preload="none" src={adjunto.url} className="mt-2.5 h-10 w-full" aria-label={`Oír ${adjunto.nombre}`} />
        {adjunto.transcripcion && (
          <p className="mt-2.5 border-t border-surface-border pt-2.5 text-sm leading-relaxed text-slate-600 [overflow-wrap:anywhere] dark:text-slate-300">
            «{adjunto.transcripcion}»
          </p>
        )}
      </div>
    );
  }

  return (
    <a
      href={adjunto.url}
      target="_blank"
      rel="noopener noreferrer"
      title={`Abrir ${adjunto.nombre}`}
      data-adjunto={adjunto.tipo}
      className={cn(CAJA, FOCO, 'flex w-72 max-w-full items-center gap-3 p-2.5 pr-3.5 transition-colors hover:border-brand-300 dark:hover:border-brand-500/60')}
    >
      <span aria-hidden className={cn('grid h-10 w-10 shrink-0 place-items-center rounded-lg', tono)}>
        <Icono className="h-5 w-5" />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block truncate text-sm font-medium text-slate-800 dark:text-slate-100">{adjunto.nombre}</span>
        <span className={DATO}>
          {tipo} · {peso}
        </span>
      </span>
      <ArrowUpRight aria-hidden className="h-4 w-4 shrink-0 text-slate-400" />
    </a>
  );
}

export type EstadoDeSubida = 'subiendo' | 'listo' | 'error';

/**
 * Un archivo que espera sobre el campo de escribir: subiendo, listo para irse con el mensaje, o
 * rechazado (con lo que dijo el servidor). La ✕ lo quita en cualquiera de los tres estados.
 */
export function FichaEnCola({
  nombre,
  tamano,
  estado,
  error,
  onQuitar,
}: {
  nombre: string;
  tamano: number;
  estado: EstadoDeSubida;
  error?: string;
  onQuitar?: () => void;
}) {
  const mal = estado === 'error';
  return (
    <li
      data-estado={estado}
      className={cn(
        'flex max-w-full items-center gap-2 rounded-xl border py-1 pl-2.5 pr-1 text-sm',
        mal ? 'border-red-200 bg-red-50 dark:border-red-900/60 dark:bg-red-950/40' : 'border-surface-border bg-surface',
      )}
    >
      {estado === 'subiendo' ? (
        <Loader2 aria-hidden className="h-4 w-4 shrink-0 text-brand-600 motion-safe:animate-spin dark:text-brand-300" />
      ) : mal ? (
        <AlertCircle aria-hidden className="h-4 w-4 shrink-0 text-red-600 dark:text-red-300" />
      ) : (
        <Check aria-hidden className="h-4 w-4 shrink-0 text-emerald-600 dark:text-emerald-400" />
      )}
      <span className="min-w-0">
        <span className="block max-w-[11rem] truncate font-medium text-slate-800 dark:text-slate-100">{nombre}</span>
        <span role={mal ? 'alert' : 'status'} className={cn('block max-w-[16rem] text-xs', mal ? 'text-red-700 dark:text-red-300' : 'text-slate-500 dark:text-slate-400')}>
          {estado === 'subiendo' ? 'Subiendo…' : mal ? error || 'No se pudo subir.' : tamanoLegible(tamano)}
        </span>
      </span>
      <button
        type="button"
        onClick={onQuitar}
        aria-label={`Quitar ${nombre}`}
        className={cn('grid h-11 w-11 shrink-0 place-items-center rounded-lg text-slate-500 transition-colors hover:bg-slate-200/70 hover:text-slate-800 md:h-8 md:w-8 dark:text-slate-400 dark:hover:bg-slate-700 dark:hover:text-slate-100', FOCO)}
      >
        <X className="h-4 w-4" />
      </button>
    </li>
  );
}

// ───────────────────────────── los botones de un mensaje ─────────────────────────────

/** Cómo están los botones de UN mensaje de Athena. Vive en la pantalla: el servidor no lo guarda. */
export interface EstadoDeAcciones {
  /** La pregunta ya se contestó («Déjalo aquí», o el chat ya se importó): lo que mueve algo se apaga. */
  resuelto?: boolean;
  /** Los botones (por su lugar) que ya hicieron lo suyo. */
  hechas?: readonly number[];
  /** El que está corriendo ahora mismo. */
  ocupada?: number | null;
  /** El que está esperando el «sí» de la persona. */
  porConfirmar?: number | null;
}

const ICONO_DE_ACCION: Record<AccionDeAthena['tipo'], LucideIcon> = {
  ir: ArrowUpRight,
  ficha: UserRound,
  llevar: FolderInput,
  importarChat: MessagesSquare,
  guardar: Check,
};

/** Abrir una pantalla o una ficha no cambia nada: se puede tocar siempre. Lo demás mueve algo. */
const mueveAlgo = (a: AccionDeAthena) => a.tipo === 'llevar' || a.tipo === 'importarChat' || a.tipo === 'guardar';

const PILDORA =
  'inline-flex min-h-[44px] max-w-full items-center gap-1.5 rounded-full border px-3.5 text-left text-sm font-medium leading-tight transition-colors md:min-h-[36px] ' +
  'disabled:cursor-not-allowed disabled:opacity-55';
const DE_MARCA =
  'border-brand-200 bg-brand-50 text-brand-700 enabled:hover:bg-brand-100 dark:border-brand-500/40 dark:bg-brand-900/30 dark:text-brand-200 dark:enabled:hover:bg-brand-900/50';
const NEUTRA =
  'border-surface-border bg-surface text-slate-700 enabled:hover:bg-slate-100 dark:border-slate-700 dark:text-slate-200 dark:enabled:hover:bg-slate-800';

function Acciones({
  acciones,
  estado,
  onAccion,
  onConfirmar,
  onCancelar,
}: {
  acciones: readonly AccionDeAthena[];
  estado: EstadoDeAcciones;
  onAccion?: (accion: AccionDeAthena, indice: number) => void;
  onConfirmar?: (accion: AccionDeAthena, indice: number) => void;
  onCancelar?: () => void;
}) {
  const corriendo = estado.ocupada != null;
  return (
    <div className="flex flex-wrap gap-2">
      {acciones.map((accion, i) => {
        if (estado.porConfirmar === i && accion.tipo === 'importarChat') {
          return (
            <div
              key={i}
              role="group"
              aria-label="¿Importo este chat?"
              className="flex max-w-full flex-wrap items-center gap-x-2 gap-y-1 rounded-3xl border border-brand-200 bg-brand-50 py-1 pl-3.5 pr-1 text-sm text-brand-800 dark:border-brand-500/40 dark:bg-brand-900/30 dark:text-brand-100"
            >
              <span className="font-medium">¿Importo este chat?</span>
              <span className="flex items-center gap-1">
                <button
                  type="button"
                  autoFocus
                  onClick={() => onConfirmar?.(accion, i)}
                  className={cn('min-h-[44px] rounded-full bg-brand-600 px-3.5 font-semibold text-white transition-colors hover:bg-brand-700 md:min-h-[32px]', FOCO)}
                >
                  {accion.yoSoy ? `Sí, soy ${accion.yoSoy}` : 'Sí, importar'}
                </button>
                <button type="button" onClick={onCancelar} className={cn('min-h-[44px] rounded-full px-3 font-medium transition-colors hover:bg-brand-100 md:min-h-[32px] dark:hover:bg-brand-900/60', FOCO)}>
                  No
                </button>
              </span>
            </div>
          );
        }
        const hecha = estado.hechas?.includes(i) ?? false;
        const ocupada = estado.ocupada === i;
        const apagada = corriendo || hecha || (Boolean(estado.resuelto) && mueveAlgo(accion));
        const Icono = ocupada ? Loader2 : hecha ? Check : ICONO_DE_ACCION[accion.tipo];
        return (
          <button
            key={i}
            type="button"
            disabled={apagada}
            aria-busy={ocupada || undefined}
            data-accion={accion.tipo}
            data-hecha={hecha ? 'si' : undefined}
            onClick={() => onAccion?.(accion, i)}
            className={cn(PILDORA, FOCO, accion.tipo === 'llevar' || accion.tipo === 'importarChat' ? DE_MARCA : NEUTRA)}
          >
            <Icono aria-hidden className={cn('h-4 w-4 shrink-0', ocupada && 'motion-safe:animate-spin')} />
            <span className="min-w-0 py-1.5">{accion.etiqueta}</span>
          </button>
        );
      })}
    </div>
  );
}

/** «Copiar» y nada más: Athena redacta, mandar es de la persona. Se copia sin los asteriscos de las negritas. */
function BotonDeCopiar({ texto }: { texto: string }) {
  const [copiado, setCopiado] = useState(false);
  return (
    <button
      type="button"
      onClick={() => {
        // Sin permiso o fuera de https el portapapeles no existe: no se dice «Copiado» si no se copió.
        void navigator.clipboard?.writeText(sinMarcas(texto)).then(
          () => {
            setCopiado(true);
            setTimeout(() => setCopiado(false), 1600);
          },
          () => undefined,
        );
      }}
      className={cn('-my-2 inline-flex min-h-[44px] items-center gap-1 rounded-md px-1 font-medium transition-colors hover:text-slate-800 md:min-h-[28px] dark:hover:text-slate-100', FOCO)}
    >
      {copiado ? <Check aria-hidden className="h-3.5 w-3.5 text-emerald-600 dark:text-emerald-400" /> : <Copy aria-hidden className="h-3.5 w-3.5" />}
      <span aria-live="polite">{copiado ? 'Copiado' : 'Copiar'}</span>
    </button>
  );
}

// ───────────────────────────── la burbuja ─────────────────────────────

export interface BurbujaDeAthenaProps {
  mensaje: AthenaMensaje;
  /** Para decir la hora: de hoy va sola; de otro día, con su fecha. */
  ahora: Date;
  /** Solo en un mensaje del usuario que todavía no llegó al servidor, o que no llegó. */
  envio?: 'enviando' | 'fallo';
  onReintentar?: () => void;
  acciones?: EstadoDeAcciones;
  onAccion?: (accion: AccionDeAthena, indice: number) => void;
  onConfirmar?: (accion: AccionDeAthena, indice: number) => void;
  onCancelar?: () => void;
}

/**
 * Un mensaje de la conversación. Los del usuario, a la derecha en el color de la marca; los de
 * Athena, a la izquierda con su avatar, su texto con forma, sus botones y «Copiar».
 */
export function BurbujaDeAthena({ mensaje, ahora, envio, onReintentar, acciones = {}, onAccion, onConfirmar, onCancelar }: BurbujaDeAthenaProps) {
  const hora = horaDeMensaje(mensaje.createdAt, ahora);

  if (mensaje.rol === 'usuario') {
    return (
      <article data-rol="usuario" data-envio={envio} aria-label="Tú" className="flex flex-col items-end gap-1.5">
        {mensaje.adjuntos.map((a) => (
          <FichaDeAdjunto key={a.id} adjunto={a} />
        ))}
        {mensaje.texto && (
          <div
            className={cn(
              'max-w-[88%] whitespace-pre-wrap rounded-2xl rounded-br-md bg-brand-600 px-4 py-2.5 text-[15px] leading-6 text-white shadow-sm shadow-brand-900/10 [overflow-wrap:anywhere] md:max-w-[80%]',
              envio === 'fallo' && 'opacity-70',
            )}
          >
            {mensaje.texto}
          </div>
        )}
        {envio === 'fallo' ? (
          <p role="alert" className="flex items-center gap-1.5 text-xs font-medium text-red-700 dark:text-red-300">
            <AlertCircle aria-hidden className="h-3.5 w-3.5" />
            No se envió
            <button type="button" onClick={onReintentar} className={cn('-my-2 inline-flex min-h-[44px] items-center gap-1 rounded-md px-1.5 font-semibold underline underline-offset-2 md:min-h-[28px]', FOCO)}>
              <RotateCw aria-hidden className="h-3.5 w-3.5" />
              Reintentar
            </button>
          </p>
        ) : (
          <p className={DATO}>{envio === 'enviando' ? 'Enviando…' : <time dateTime={mensaje.createdAt}>{hora}</time>}</p>
        )}
      </article>
    );
  }

  return (
    <article data-rol="athena" aria-label="Athena" className="flex items-start gap-3">
      <AvatarDeAthena className="mt-0.5" />
      <div className="min-w-0 flex-1 space-y-2.5">
        {/* 36rem de ancho: unas 75 letras por renglón, que es lo que se lee cómodo. */}
        {mensaje.texto && (
          <div className="w-fit max-w-[min(100%,36rem)] rounded-2xl rounded-tl-md border border-surface-border bg-surface px-4 py-3 text-[15px] leading-6 text-slate-800 shadow-sm dark:text-slate-100">
            <TextoRico texto={mensaje.texto} />
          </div>
        )}
        {mensaje.adjuntos.map((a) => (
          <FichaDeAdjunto key={a.id} adjunto={a} />
        ))}
        {mensaje.acciones.length > 0 && <Acciones acciones={mensaje.acciones} estado={acciones} onAccion={onAccion} onConfirmar={onConfirmar} onCancelar={onCancelar} />}
        <div className={cn(DATO, 'flex items-center gap-3')}>
          <time dateTime={mensaje.createdAt}>{hora}</time>
          {mensaje.texto && <BotonDeCopiar texto={mensaje.texto} />}
        </div>
      </div>
    </article>
  );
}

/** Athena está contestando: tres puntos que suben y bajan (quietos si la persona pidió menos movimiento). */
export function Escribiendo() {
  return (
    <div role="status" data-escribiendo className="flex items-start gap-3 motion-safe:animate-fade-in">
      <AvatarDeAthena className="mt-0.5" />
      <div className="flex h-[46px] items-center gap-1.5 rounded-2xl rounded-tl-md border border-surface-border bg-surface px-4 shadow-sm">
        <span className="sr-only">Athena está escribiendo…</span>
        {[0, 160, 320].map((retraso) => (
          <span
            key={retraso}
            aria-hidden
            style={{ animationDelay: `${retraso}ms` }}
            className="h-2 w-2 rounded-full bg-brand-400 motion-safe:animate-bounce motion-safe:[animation-duration:1.1s]"
          />
        ))}
      </div>
    </div>
  );
}

// ───────────────────────────── la pantalla vacía ─────────────────────────────

export type IdDeSugerencia = 'me-toca' | 'buscar' | 'subir' | 'ayuda';

const SUGERENCIAS: ReadonlyArray<{ id: IdDeSugerencia; etiqueta: string; pista: string; icono: LucideIcon }> = [
  { id: 'me-toca', etiqueta: '¿Qué me toca hoy?', pista: 'Lo vencido y lo de hoy', icono: Inbox },
  { id: 'buscar', etiqueta: 'Busca a un cliente', pista: 'Por nombre o teléfono', icono: Search },
  { id: 'subir', etiqueta: 'Sube un flyer', pista: 'PDF, fotos, audios y chats', icono: FileUp },
  { id: 'ayuda', etiqueta: '¿Qué puedes hacer?', pista: 'Con la clave de IA y sin ella', icono: HelpCircle },
];

/** Por dónde empezar: cuatro botones grandes. Qué hace cada uno lo decide la página. */
export function Sugerencias({ onElegir, apagadas = false }: { onElegir: (id: IdDeSugerencia) => void; apagadas?: boolean }) {
  return (
    <ul aria-label="Para empezar" className="grid grid-cols-2 gap-2.5 sm:gap-3">
      {SUGERENCIAS.map(({ id, etiqueta, pista, icono: Icono }) => (
        <li key={id} className="flex">
          <button
            type="button"
            disabled={apagadas}
            data-sugerencia={id}
            onClick={() => onElegir(id)}
            className={cn(
              'group flex min-h-[76px] w-full items-start gap-3 rounded-2xl border border-surface-border bg-surface p-3 text-left shadow-sm transition duration-200 sm:p-3.5',
              'enabled:hover:-translate-y-0.5 enabled:hover:border-brand-300 enabled:hover:shadow-md motion-reduce:enabled:hover:translate-y-0 disabled:opacity-60 dark:enabled:hover:border-brand-500/60',
              FOCO,
            )}
          >
            <span aria-hidden className="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-brand-50 text-brand-600 transition-colors group-enabled:group-hover:bg-brand-600 group-enabled:group-hover:text-white dark:bg-brand-900/40 dark:text-brand-200">
              <Icono className="h-[18px] w-[18px]" />
            </span>
            <span className="min-w-0">
              <span className="block text-sm font-semibold leading-snug text-slate-900 dark:text-slate-50">{etiqueta}</span>
              <span className="mt-0.5 block text-xs leading-snug text-slate-500 dark:text-slate-400">{pista}</span>
            </span>
          </button>
        </li>
      ))}
    </ul>
  );
}

/**
 * «Tu mañana» y «Tu cierre»: lo que Athena sabe sin que le pregunten. Una sola pieza partida en
 * dos, no dos tarjetas sueltas. Los textos los calcula la página.
 */
export function ResumenDelDia({ manana, cierre }: { manana: string; cierre: string }) {
  const mitad = 'flex min-w-0 flex-1 items-start gap-3 p-3.5 sm:p-4';
  const icono = 'mt-0.5 h-[18px] w-[18px] shrink-0';
  return (
    <section
      aria-label="Tu día"
      className="flex flex-col divide-y divide-surface-border rounded-2xl border border-surface-border bg-surface/70 sm:flex-row sm:divide-x sm:divide-y-0"
    >
      <div className={mitad}>
        <Sunrise aria-hidden className={cn(icono, 'text-amber-500')} />
        <div className="min-w-0">
          <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-50">Tu mañana</h3>
          <p className="mt-0.5 text-sm leading-snug text-slate-600 dark:text-slate-300">{manana}</p>
        </div>
      </div>
      <div className={mitad}>
        <Moon aria-hidden className={cn(icono, 'text-brand-500 dark:text-brand-300')} />
        <div className="min-w-0">
          <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-50">Tu cierre</h3>
          <p className="mt-0.5 text-sm leading-snug text-slate-600 dark:text-slate-300">{cierre}</p>
        </div>
      </div>
    </section>
  );
}
